<template>
  <b-modal
    id="duplicate-contacts-modal"
    ref="duplicate-contacts-modal"
    title="Contact May Already Exist"
    header-class="alert-danger"
    footer-class="d-flex justify-content-end"
    size="xl"
    scrollable
  >
    <div v-if="contact" class="current-contact">
      <p>{{ editing ? 'Current Contact:' : 'Contact Information Provided:' }}</p>
      <div v-if="editing">
        Contact Id:
        <strong>{{ contact.contactId }}</strong>
      </div>
      <div>
        Name:
        <strong>{{ contact.firstName }} {{ contact.lastName }}</strong>
      </div>
      <div>
        Email:
        <strong>{{ contact.emailAddress }}</strong>
      </div>
      <div>
        Phone:
        <strong>{{ contact.phoneNumber | phone }}</strong>
      </div>
      <div v-if="editing" class="d-flex">
        Current Contact Customer Id(s):
        <span class="ml-1">
          <strong>{{ contact.customerMaps ? contact.customerMaps.map(map => map.customerId).join(', ') : '' }}</strong>
        </span>
      </div>
    </div>
    <div class="mt-2 mb-2">
      <p>
        We found contacts matching the
        {{ editing ? 'contact you are currently editing' : 'information you entered' }}. If the correct contact is
        listed below, select it and click
        <strong>
          {{ isServiceOrder ? 'Update Repair Contact.' : editing ? 'Use Selected Contact.' : 'Add Selected Contact.' }}
        </strong>
        The selected contact will be added {{ isServiceOrder ? 'to the record and' : '' }} to the Customer account. The
        contact you
        {{
          editing
            ? ' are currently editing will be automatically deactivated to help prevent future duplicate warnings.'
            : 'were creating will be discarded.'
        }}
        If none of the matches are correct, click
        <strong>{{ editing ? 'Continue Editing Contact' : 'Create New Contact' }}</strong>
        {{ editing ? ' to return to the contact you are currently editing.' : 'to continue.' }}
      </p>
    </div>
    <b-table
      class="align-middle border mt-3"
      striped
      sticky-header="400px"
      no-border-collapse
      :items="duplicates"
      :fields="fields"
      show-empty
      empty-text=""
    >
      <template #cell(select)="{ item }">
        <div>
          <b-form-checkbox v-model="selectedContact" :value="item" />
        </div>
      </template>
      <template #cell(firstName)="{ item }">
        <div :class="{ 'highlight-data': isMatch(item.firstName, contact.firstName) }">
          {{ item.firstName }}
        </div>
      </template>
      <template #cell(lastName)="{ item }">
        <div :class="{ 'highlight-data': isMatch(item.lastName, contact.lastName) }">
          {{ item.lastName }}
        </div>
      </template>
      <template #cell(emailAddress)="{ item }">
        <div :class="{ 'highlight-data': isMatch(item.emailAddress, contact.emailAddress) }">
          {{ item.emailAddress }}
        </div>
      </template>
      <template #cell(customerMaps)="{ item }">
        <div v-for="map in item.customerMaps" :key="map.customerId">{{ map.customerId }} - {{ map.customerName }}</div>
      </template>
      <template #cell(phoneNumber)="{ item }">
        <div :class="{ 'highlight-data': isMatch(item.phoneNumber, contact.phoneNumber) }">
          {{ item.phoneNumber | phone }}
        </div>
      </template>
    </b-table>
    <template #modal-footer>
      <b-button
        size="sm"
        variant="primary"
        class="mr-2"
        :disabled="!selectedContact"
        @click="isServiceOrder ? updateServiceOrderContact() : updateCustomerContact()"
      >
        {{ getButtonLabel() }}
      </b-button>
      <b-button size="sm" @click="userOverride()">
        {{ isServiceOrder ? 'Create New Contact' : editing ? 'Continue Editing Contact' : 'Create New Contact' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { ServiceOrderMutations } from '@/shared/store/service-order/types';
import { CustomerActions, CustomerGetters } from '@/shared/store/customer/types';
import ErrorService from '@/shared/services/ErrorService';

export default {
  name: 'DuplicateContactsModal',
  props: {
    duplicates: {
      type: Array,
      default: null
    },
    contact: {
      type: Object,
      default: null
    },
    isServiceOrder: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      fields: [
        { key: 'select', label: 'Select', sortable: false },
        { key: 'contactId', label: 'Contact ID', sortable: false },
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'emailAddress', label: 'Email Address', sortable: true },
        { key: 'phoneNumber', label: 'Phone Number', sortable: true },
        { key: 'customerMaps', label: 'Customers', sortable: false }
      ],
      selectedContact: null
    };
  },
  computed: {
    ...mapGetters({ customer: CustomerGetters.GET_CUSTOMER })
  },
  methods: {
    ...mapActions([CustomerActions.ADD_OR_UPDATE_CONTACT]),
    ...mapMutations([ServiceOrderMutations.SET_PROP]),
    /**
     * @param that - scope of component that is using the modal
     * @param save - async function that saves data
     */
    show(that, save) {
      this.$bvModal.show('duplicate-contacts-modal');
      this.$nextTick(() => {
        this.$refs['duplicate-contacts-modal'].$once('hide', bvEvent => {
          this.$refs['duplicate-contacts-modal'].$once('hidden', async () => handleHide(bvEvent, this, save));
        });
      });
    },
    severityText(value) {
      return value.alertSeverity;
    },
    getButtonLabel() {
      if (this.isServiceOrder) {
        return 'Update Repair Contact';
      } else if (this.editing && this.selectedContact) {
        if (this.selectedContact.customerMaps.find(map => map.customerId === this.customer.customerId)) {
          return 'Use Selected Contact';
        }
        return 'Add Selected Contact';
      } else {
        if (this.editing) {
          return 'Use Selected Contact';
        }
        return 'Add Selected Contact';
      }
    },
    updateServiceOrderContact() {
      let value = { ...this.selectedContact };
      this[ServiceOrderMutations.SET_PROP]({ key: 'repairContact', value });
      this.$bvModal.hide('duplicate-contacts-modal');
    },
    async updateCustomerContact() {
      try {
        let contact = { ...this.selectedContact };
        if (!contact.firstName && this.contact?.firstName) {
          contact.firstName = this.contact.firstName;
        }
        if (!contact.lastName && this.contact?.lastName) {
          contact.lastName = this.contact.lastName;
        }
        if (!contact.emailAddress && this.contact?.emailAddress) {
          contact.emailAddress = this.contact.emailAddress;
        }
        if (!contact.phoneNumber && this.contact?.phoneNumber) {
          contact.phoneNumber = this.contact.phoneNumber;
        }
        await this[CustomerActions.ADD_OR_UPDATE_CONTACT]({ contact: contact, customerId: this.customer.customerId });
        this.$bvModal.hide('duplicate-contacts-modal');
        this.$emit('contact-updated', contact);
      } catch (error) {
        ErrorService.createErrorToast(this, 'An error occurred while updating the contact.');
      }
    },
    userOverride() {
      this.$bvModal.hide('duplicate-contacts-modal');
      this.$emit('user-override');
    },
    isMatch(val1, val2) {
      if (!val1 || !val2) return false;
      return String(val1).trim().toLowerCase() === String(val2).trim().toLowerCase();
    }
  }
};
async function handleHide(bvEvent, that, save) {
  if (bvEvent.trigger === 'ok' && save != null) {
    await save();
  }
}
</script>
<style>
.modal-input-group {
  margin-top: 5px;
  display: flex;
  align-items: center;
}
.modal-input {
  margin-left: 10px;
}
.highlight-data {
  font-weight: bold;
  border-bottom: 4px solid #ffc107;
}
.current-contact {
  font-size: 1em;
}
</style>
